import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router';
import { DownloadOutlined } from '@ant-design/icons';
import '@ant-design/compatible/assets/index.css';
import { Col } from 'antd';
import {
	assetsRestCrudThunksForBuyer,
	assetsSearchAndAPIAdaptor,
	downloadAssetCSVForBuyer,
} from '../../thunks/assets_thunks';
import posthog from 'posthog-js';
import { EmptyState } from '../empty_state/EmptyState';
import { ASSETS_CRUD_ACTION_CREATORS } from '../../actions/assets_actions';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { PAGE_SIZE_OPTIONS } from '../../constants/pagination';
import { ROLE_TYPES } from '../../utils/DataConstants';
import AssetWithQRCodeDisplay from '../asset_row_display/AssetWithQRCodeDisplay';
import { getBuyerCompanySettingValue, PERMISSION_NAMES } from '../../utils/AuthUtils';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { INITIAL_ACTIVE_FILTERS } from '../../utils/FilterUtils';
import ReduxDataTable from '../redux_data_table/ReduxDataTable';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import OWButtonWithSupplierManagedLock from '../common/OWButtonWithSupplierManagedLock';
import { useFilters } from '../../hooks/useFilters';
import { getAssetFilterConfig } from '../../utils/AssetFilterUtils';

const queryString = require('qs');

require('./AssetsIndexPage.less');

const TC_NAME = 'assetsIndex';
const ASSET_ADVANCED_FILTERS = 'assetsAdvancedFilters';

const AssetsIndexPage: FC<any> = ({
	history,
	location,
	assets,
	companyConfig,
	updateAssetsFilters,
	downloadAssetCSV,
}) => {
	const currentUser = useSelector((state: any) => state.session.currentUser);
	let searchString = location.search;
	if (searchString[0] === '?') {
		searchString = searchString.slice(1);
	}
	const queryParams = queryString.parse(searchString);
	const {
		current,
		pageSize,
		/* eslint-disable @typescript-eslint/no-unused-vars */
		total,
		pageSizeOptions,
		showSizeChanger,
		onShowSizeChange,
		sort_by,
		order,
		/* eslint-enable @typescript-eslint/no-unused-vars */
		...assetFilters
	} = queryParams;

	const initialPagination = useMemo(
		() => ({
			current: current ? current : 1,
			pageSize: pageSize ? pageSize : posthog.isFeatureEnabled('assets-openwsearch') ? 50 : 10,
		}),
		[]
	);
	const initialFilters = useMemo(() => assetFilters || {}, []);

	useEffect(() => {
		updateAssetsFilters(initialFilters, TC_NAME);
		history.push(`/buyer/assets/overview/all?${new URLSearchParams(location.search)}`);
	}, []);

	const downloadCSV = useCallback(() => {
		const filters = assets[TC_NAME].filters;
		downloadAssetCSV({}, filters);
	}, [downloadAssetCSV, assets]);

	const onRow = useCallback(
		(record) => ({
			onClick: () => {
				const path = `/buyer/assets/detail/${nullSafeGet(
					'id',
					record
				)}/details?targetCollectionName=${TC_NAME}`;
				history.push(
					getLinkWIthBackLinkParams(location, 'Back to Assets', path, {
						preserve: true,
					})
				);
			},
		}),
		[history, location]
	);

	const columns = useMemo(
		() => [
			{
				title: 'Name',
				dataIndex: 'name',
				render: (text, record) => <AssetWithQRCodeDisplay asset={record} />,
			},
		],
		[]
	);

	const allowArea = getBuyerCompanySettingValue(ROLE_TYPES.BUYER)(
		'assetConfig.allowAreaAssociationWithAssets',
		companyConfig,
		{},
		false
	);

	const removeAssetNumberToQRCodeAssociation = getBuyerCompanySettingValue(ROLE_TYPES.BUYER)(
		'assetConfig.removeAssetNumberToQRCodeAssociation',
		companyConfig,
		{},
		false
	);

	const updateFilters = useCallback(
		(filters) => updateAssetsFilters(filters, TC_NAME),
		[updateAssetsFilters]
	);

	const allFilters = useFilters({
		filterTargetCollectionName: ASSET_ADVANCED_FILTERS,
		overviewCollectionName: TC_NAME,
		updateFilters,
	});

	const replacementIndexEnabled = !!posthog.isFeatureEnabled('replacement-index');

	const fetchDataProp = useMemo(
		() =>
			posthog.isFeatureEnabled('assets-openwsearch')
				? {
						...assetsRestCrudThunksForBuyer,
						read: (
							params,
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						) =>
							assetsRestCrudThunksForBuyer.searchWithPostMethod(
								filters ? { ...(params || {}), ...filters } : params,
								targetCollectionName,
								pagination,
								sorting,
								filters,
								addToTargetCollection,
								assetsSearchAndAPIAdaptor
							),
				  }
				: assetsRestCrudThunksForBuyer,
		[]
	);

	const filterConfig = useMemo(
		() =>
			getAssetFilterConfig({
				filters: allFilters,
				allowArea,
				currentUser,
				removeAssetNumberToQRCodeAssociation,
				replacementIndexEnabled,
			}),
		[
			allFilters,
			allowArea,
			currentUser,
			removeAssetNumberToQRCodeAssociation,
			replacementIndexEnabled,
		]
	);

	return (
		<ReduxDataTable
			rightActions={
				<Col style={{ textAlign: 'right' }}>
					<AccessPermissionChecker name={PERMISSION_NAMES.SHOW_ASSET_PRICE}>
						<OWButtonWithSupplierManagedLock
							style={{ marginRight: 12 }}
							size="large"
							icon={<DownloadOutlined />}
							className="inline-block-visible-md"
							onClick={downloadCSV}
						>
							<span className="inline-block-visible-xxl">Export CSV</span>
						</OWButtonWithSupplierManagedLock>
					</AccessPermissionChecker>
					<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_ASSETS}>
						<OWButtonWithSupplierManagedLock
							size="large"
							type="primary"
							onClick={() => history.push('/buyer/assets/overview/new')}
						>
							Add Asset
						</OWButtonWithSupplierManagedLock>
					</AccessPermissionChecker>
				</Col>
			}
			handleQueryParams
			filterConfig={filterConfig}
			emptyState={
				<EmptyState
					graphic={
						<img
							alt="Currently there are no assets!"
							style={{ marginBottom: 8 }}
							src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
						/>
					}
					headline={"It's a little lonely in here."}
					body={
						<div style={{ textAlign: 'center' }}>
							<div style={{ maxWidth: 440, marginBottom: 16 }}>
								Looks like your team has not added any assets yet.
							</div>
							<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_ASSETS}>
								<OWButtonWithSupplierManagedLock
									type="primary"
									onClick={() => history.push('/buyer/assets/overview/new')}
								>
									Add your first asset
								</OWButtonWithSupplierManagedLock>
							</AccessPermissionChecker>
						</div>
					}
				/>
			}
			collectionName="assets"
			onTableRow={onRow}
			showHeader={false}
			targetCollectionName={TC_NAME}
			tableColumns={columns}
			keyAccessor={(el) => el.id}
			initialFilters={{ ...initialFilters, ...INITIAL_ACTIVE_FILTERS }}
			initialPagination={initialPagination}
			hiddenPagination={{ showSizeChanger: true, pageSizeOptions: PAGE_SIZE_OPTIONS }}
			fetchData={fetchDataProp}
			stateSlice={assets}
			entityCollectionName="assets"
			initialSorting={{ sort_by: 'name', order: 'ascend' }}
			preAppliedFilters={{ noActiveFilter: true }}
		/>
	);
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	assets: state.assets,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch) => ({
	updateAssetsFilters: (filters, targetCollection) =>
		dispatch(ASSETS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)),
	downloadAssetCSV: (params, filters) => dispatch(downloadAssetCSVForBuyer(params, filters)),
});

export default withRouter<any, any>(connect(mapStateToProps, mapDispatchToProps)(AssetsIndexPage));
